// src/pages/MorePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCalculator,
  FaChartBar,
  FaCog,
  FaFileInvoiceDollar,
  FaHandsHelping,
  FaQuestionCircle,
  FaSearch,
  FaShoppingBasket,
  FaStore,
  FaTimes,
  FaUserFriends,
  FaUserShield,
  FaUtensils,
} from "react-icons/fa";
import { getStoredUser } from "../utils/impersonation";
import { canAccessRoute } from "../utils/rbac";
import { canShowRoleUiNavigation, ROLE_UI_PERMISSIONS_EVENT } from "../utils/roleUiPermissions";
import { APP_ESCAPE_EVENT, APP_SHORTCUT_EVENT, focusAndSelect } from "../utils/shortcuts";

const CARDS = [
  { title: "Smart Business Insights", path: "/smart-insights", desc: "Business Assistant and Menu Intelligence", category: "Reports" },
  { title: "Loyalty Settings", path: "/loyalty-settings", desc: "Points and customer reward rules", category: "Customers" },
  {
    title: "Dashboard",
    path: "/dashboard",
    desc: "Key metrics & active orders",
    category: "Sales",
  },
  { title: "POS Sales", path: "/tables", desc: "Open tables and start billing", category: "Sales", permissionKey: "TableScreen" },
  { title: "Sales List", path: "/sales", desc: "View completed and active bills", category: "Sales", permissionKey: "SalesListScreen" },
  { title: "Billing History", path: "/billing/history", desc: "Browse previous billing activity", category: "Sales", permissionKey: "BillingHistory" },
  {
    title: "Hourly Sales",
    path: "/reports/hourly-sales",
    desc: "Heatmap by hour/date",
    category: "Reports",
  },
  {
    title: "Top Products",
    path: "/reports/top-products",
    desc: "Quantity + gross leaders",
    category: "Reports",
  },
  {
    title: "Top Waiters",
    path: "/reports/top-waiters",
    desc: "Waiter performance summary",
    category: "Reports",
  },
  {
    title: "Category Performance",
    path: "/reports/category-performance",
    desc: "Category mix + contribution",
    category: "Reports",
  },
  {
    title: "Table Utilization",
    path: "/reports/table-utilization",
    desc: "Table stats for today",
    category: "Reports",
  },
  {
    title: "Table-wise Sales List",
    path: "/reports/table-wise-sales-list",
    desc: "Bills grouped by table",
    category: "Reports",
  },
  {
    title: "Table-wise Product Sales",
    path: "/reports/table-wise-product-sales",
    desc: "Product count by selected tables",
    category: "Reports",
  },
  {
    title: "Finance Tracker",
    path: "/finance/tracker",
    desc: "Expense/income tracker",
    category: "Finance",
  },
  {
    title: "Finance Report",
    path: "/finance/report",
    desc: "Detailed finance report + download",
    category: "Finance",
  },
  {
    title: "Profit & Loss Report",
    path: "/finance/profit-loss",
    desc: "Sales and other income minus expenses",
    category: "Finance",
  },
  {
    title: "Day Book Entry Report",
    path: "/finance/day-book",
    desc: "Day-wise sales, income and expenses",
    category: "Finance",
  },
  {
    title: "Khatabook",
    path: "/khatabook",
    desc: "Ledger view for parties",
    category: "Finance",
  },
  {
    title: "Party Bills",
    path: "/party-bills",
    desc: "Party wise invoice list",
    category: "Parties",
  },
  { title: "Add Category", path: "/add-category", desc: "Create and manage menu categories", category: "Menu", permissionKey: "AddCategory" },
  { title: "Manage Products", path: "/add-product", desc: "Add and manage menu products", category: "Menu", permissionKey: "AddProduct" },
  { title: "Product List", path: "/add-product", desc: "View and manage menu products", category: "Menu", permissionKey: "ProductList" },
  { title: "Party / Customers", path: "/add-party", desc: "Manage customers and suppliers", category: "Parties", permissionKey: "AddParty" },
  { title: "Table Management", path: "/add-table", desc: "Add and manage tables", category: "Operations", permissionKey: "AddTable" },
  {
    title: "Menu Scanner",
    path: "/menu-scanner",
    desc: "AI menu scanning",
    category: "Operations",
  },
  {
    title: "QR Management",
    path: "/qr/management",
    desc: "Generate + manage QR codes",
    category: "Operations",
  },
  { title: "QR Table Orders", path: "/qr/table-order", desc: "Review incoming QR orders", category: "Operations", permissionKey: "QrTableOrder" },
  { title: "Calculator", path: "/calculator", desc: "Open the billing calculator", category: "Tools", permissionKey: "Calculator" },
  { title: "Role Permissions", path: "/role-permissions", desc: "Review role access settings", category: "Admin", permissionKey: "RolePermissions" },
  { title: "Profile", path: "/profile", desc: "Restaurant and account profile", category: "Account", permissionKey: "Profile" },
  { title: "Connect Bluetooth", path: "/connect-bluetooth", desc: "Connect a Bluetooth printer", category: "Operations", permissionKey: "ConnectBlueTooth" },
  {
    title: "Support Chat",
    path: "/support",
    desc: "Chat with BillSarthi support",
    category: "Help",
  },
  {
    title: "Deleted Orders",
    path: "/deleted-orders",
    desc: "Restore removed bills quickly",
    category: "Sales",
  },
  {
    title: "Offline Sales Sync",
    path: "/offline-sales-sync",
    desc: "Save up to one month of live sales for desktop offline use",
    category: "Sales",
  },
  {
    title: "Help Center",
    path: "/help",
    desc: "Quick answers + FAQs",
    category: "Help",
  },
  {
    title: "Keyboard Shortcuts",
    path: "/keyboard-shortcuts",
    desc: "Understand all web and desktop keyboard combos",
    category: "Help",
  },
  {
    title: "Login Help",
    path: "/login-help",
    desc: "Troubleshooting for signup/login",
    category: "Help",
  },
  {
    title: "Policies",
    path: "/policies",
    desc: "Refund, privacy and terms",
    category: "Help",
  },
  {
    title: "Printer / POS Settings",
    path: "/printer-settings",
    desc: "Printing, billing, and POS preferences",
    category: "Operations",
  },
  {
    title: "Product Reorder",
    path: "/product-reorder",
    desc: "Change product display order",
    category: "Operations",
  },
  {
    title: "Feedback & Bugs",
    path: "/feedback",
    desc: "Report bugs or requests",
    category: "Help",
  },
  {
    title: "Reset Account",
    path: "/reset-account",
    desc: "Danger zone: clear business data",
    category: "Admin",
  },
];

const CATEGORY_META = {
  Sales: { icon: FaShoppingBasket, color: "var(--app-primary)", soft: "#eaf5ff" },
  Reports: { icon: FaChartBar, color: "#7c3aed", soft: "#f3edff" },
  Finance: { icon: FaFileInvoiceDollar, color: "#15803d", soft: "#ebf8ef" },
  Customers: { icon: FaUserFriends, color: "#c2410c", soft: "#fff1e8" },
  Parties: { icon: FaHandsHelping, color: "#0f766e", soft: "#e8f8f5" },
  Menu: { icon: FaUtensils, color: "#be123c", soft: "#fff0f3" },
  Operations: { icon: FaCog, color: "#475569", soft: "#eef2f6" },
  Tools: { icon: FaCalculator, color: "var(--app-primary)", soft: "#e9f6fc" },
  Admin: { icon: FaUserShield, color: "#b91c1c", soft: "#fff0f0" },
  Account: { icon: FaStore, color: "#4338ca", soft: "#efefff" },
  Help: { icon: FaQuestionCircle, color: "#a16207", soft: "#fff8df" },
};

const MorePage = () => {
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const searchInputRef = useRef(null);
  const user = getStoredUser();
  const [, setPermissionVersion] = useState(0);
  useEffect(() => {
    const refresh = () => setPermissionVersion((version) => version + 1);
    window.addEventListener(ROLE_UI_PERMISSIONS_EVENT, refresh);
    return () => window.removeEventListener(ROLE_UI_PERMISSIONS_EVENT, refresh);
  }, []);
  const accessibleCards = useMemo(
    () => CARDS.filter((card) => canAccessRoute(card.path, user) && canShowRoleUiNavigation(card.path, user, "more", card.permissionKey)),
    [user]
  );

  const filteredCards = useMemo(() => {
    const q = query.trim().toLowerCase();
    return accessibleCards.filter((card) => {
      if (activeCategory !== "All" && card.category !== activeCategory) return false;
      if (!q) return true;
      const hay = `${card.title} ${card.desc} ${card.category} ${card.path}`.toLowerCase();
      return hay.includes(q);
    });
  }, [accessibleCards, activeCategory, query]);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(accessibleCards.map((card) => card.category)))],
    [accessibleCards]
  );

  useEffect(() => {
    const onShortcut = (event) => {
      switch (event?.detail?.id) {
        case "more.search":
          event.preventDefault();
          setShowSearch(true);
          setTimeout(() => {
            focusAndSelect(searchInputRef.current);
          }, 50);
          return;
        case "more.open-first-result":
          if (
            !filteredCards.length ||
            (!query.trim() && document.activeElement !== searchInputRef.current)
          ) {
            return;
          }
          event.preventDefault();
          navigate(filteredCards[0].path);
          return;
        default:
          return;
      }
    };

    window.addEventListener(APP_SHORTCUT_EVENT, onShortcut);
    return () => window.removeEventListener(APP_SHORTCUT_EVENT, onShortcut);
  }, [filteredCards, navigate, query]);

  useEffect(() => {
    const onEscape = (event) => {
      const isFocused = document.activeElement === searchInputRef.current;
      if (!showSearch && !isFocused && !query.trim()) return;
      if (!isFocused && !query.trim() && showSearch) {
        event.preventDefault();
        setShowSearch(false);
        return;
      }
      if (isFocused || query.trim()) {
        event.preventDefault();
        searchInputRef.current?.blur?.();
        if (query.trim()) setQuery("");
      }
    };

    window.addEventListener(APP_ESCAPE_EVENT, onEscape);
    return () => window.removeEventListener(APP_ESCAPE_EVENT, onEscape);
  }, [query, showSearch]);

  return (
    <Page>
      <Inner>
        <Hero>
          <HeaderRow>
            <HeaderCopy>
              <Eyebrow>WORKSPACE</Eyebrow>
            <HeaderTitle>Quick Access</HeaderTitle>
            <HeaderSubtitle>
                Everything you need to manage sales, reports and restaurant operations.
            </HeaderSubtitle>
            </HeaderCopy>
            <ResultCount>
              <strong>{accessibleCards.length}</strong>
              <span>available tools</span>
            </ResultCount>
          </HeaderRow>

          {showSearch && (
            <SearchWrap>
              <FaSearch aria-hidden />
            <SearchInput
              ref={searchInputRef}
              data-shortcut-search="page"
              type="text"
              placeholder="Search shortcuts… (e.g., add)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
              {query ? (
                <ClearSearch type="button" aria-label="Clear search" onClick={() => setQuery("")}>
                  <FaTimes />
                </ClearSearch>
              ) : <SearchHint>Type to search</SearchHint>}
            </SearchWrap>
          )}
        </Hero>

        <CategoryBar aria-label="Filter shortcuts by category">
          {categories.map((category) => (
            <CategoryButton
              key={category}
              type="button"
              $active={activeCategory === category}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </CategoryButton>
          ))}
        </CategoryBar>

        <SectionHead>
          <div>
            <h2>{activeCategory === "All" ? "All shortcuts" : activeCategory}</h2>
            <p>{filteredCards.length} {filteredCards.length === 1 ? "result" : "results"}</p>
          </div>
          {!showSearch ? (
            <SearchBtn type="button" onClick={() => setShowSearch(true)}>
              <FaSearch /> Search
            </SearchBtn>
          ) : null}
        </SectionHead>

        <CardGrid>
          {filteredCards.map((card) => {
            const meta = CATEGORY_META[card.category] || CATEGORY_META.Operations;
            const CardIcon = meta.icon;
            return (
              <Card key={card.title} $color={meta.color} onClick={() => navigate(card.path)}>
                <CardIconBox $color={meta.color} $soft={meta.soft}>
                  <CardIcon />
                </CardIconBox>
                <CardContent>
                  <Chip $color={meta.color} $soft={meta.soft}>{card.category}</Chip>
                  <Title>{card.title}</Title>
                  <Desc>{card.desc}</Desc>
                </CardContent>
                <IconCircle $color={meta.color}>
                  <FaArrowRight />
                </IconCircle>
              </Card>
            );
          })}
        </CardGrid>
        {!filteredCards.length ? (
          <EmptyState>
            <FaSearch />
            <h3>No shortcuts found</h3>
            <p>Try a different search term or category.</p>
            <button type="button" onClick={() => { setQuery(""); setActiveCategory("All"); }}>Clear filters</button>
          </EmptyState>
        ) : null}
      </Inner>
    </Page>
  );
};

export default MorePage;

/* -------- styled -------- */

const Page = styled.div`
  min-height: 100vh;
  box-sizing: border-box;
  background: #f4f7fb;
  padding: 28px 22px 48px;
  display: flex;
  justify-content: center;

  @media (max-width: 640px) { padding: 14px 12px 32px; }
`;

const Inner = styled.div`
  width: 100%;
`;

const Hero = styled.section`
  padding: 28px;
  border: 1px solid #cfe3f5;
  border-radius: 22px;
  background:
    radial-gradient(circle at 90% 0%, rgba(14, 165, 233, 0.14), transparent 34%),
    linear-gradient(135deg, #ffffff 0%, #eef7ff 100%);
  box-shadow: 0 12px 34px rgba(15, 70, 110, 0.08);

  @media (max-width: 640px) { padding: 20px 16px; border-radius: 16px; }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
`;

const HeaderCopy = styled.div`max-width: 720px;`;
const Eyebrow = styled.div`
  margin-bottom: 7px;
  color: var(--app-primary);
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .16em;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: clamp(26px, 3vw, 38px);
  line-height: 1.1;
  font-weight: 900;
  color: #0f172a;
`;

const HeaderSubtitle = styled.p`
  margin: 9px 0 0;
  max-width: 620px;
  font-size: 15px;
  line-height: 1.55;
  color: #526176;
`;

const ResultCount = styled.div`
  min-width: 126px;
  padding: 12px 16px;
  border: 1px solid #d7e7f5;
  border-radius: 14px;
  background: rgba(255,255,255,.76);
  text-align: center;
  strong, span { display: block; }
  strong { color: var(--app-primary); font-size: 24px; font-weight: 900; }
  span { margin-top: 2px; color: #64748b; font-size: 11px; font-weight: 700; }
`;

const SearchBtn = styled.button`
  min-height: 38px;
  border: 1px solid #d7e2ee;
  background: #ffffff;
  color: var(--app-primary);
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 700;
  font-size: 12px;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
  }
`;

const SearchWrap = styled.div`
  position: relative;
  width: min(720px, 100%);
  margin-top: 22px;
  > svg { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: var(--app-primary); }
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  min-height: 50px;
  border: 1px solid #c8d9e8;
  border-radius: 13px;
  padding: 0 130px 0 46px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  outline: none;
  background: #fff;
  &:focus {
    border-color: var(--app-primary);
    box-shadow: 0 0 0 4px rgba(11, 120, 196, 0.12);
  }
`;

const SearchHint = styled.span`
  position: absolute; right: 15px; top: 50%; transform: translateY(-50%);
  color: #94a3b8; font-size: 11px; font-weight: 700; pointer-events: none;
`;
const ClearSearch = styled.button`
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  width: 32px; height: 32px; display: grid; place-items: center;
  border: 0; border-radius: 9px; background: #eef5fb; color: #526176; cursor: pointer;
`;

const CategoryBar = styled.div`
  display: flex; gap: 8px; margin: 22px 0 18px; padding-bottom: 4px;
  overflow-x: auto; scrollbar-width: thin;
`;
const CategoryButton = styled.button`
  flex: 0 0 auto; min-height: 38px; padding: 0 15px;
  border: 1px solid ${({ $active }) => ($active ? "var(--app-primary)" : "#dce4ed")};
  border-radius: 999px;
  background: ${({ $active }) => ($active ? "var(--app-primary)" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#526176")};
  font-size: 12px; font-weight: 800; cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? "0 7px 16px rgba(7,95,158,.18)" : "none")};
`;

const SectionHead = styled.div`
  display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; margin-bottom: 12px;
  h2 { margin: 0; color: #0f172a; font-size: 19px; font-weight: 900; }
  p { margin: 3px 0 0; color: #7b8798; font-size: 12px; }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  @media (max-width: 980px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Card = styled.button`
  position: relative;
  min-width: 0;
  min-height: 128px;
  border: 1px solid #dfe6ee;
  border-radius: 16px;
  padding: 17px 52px 17px 17px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  align-items: start;
  gap: 13px;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.09);
    border-color: ${({ $color }) => $color};
  }
  &:focus-visible { outline: 3px solid rgba(11,120,196,.2); outline-offset: 2px; }
`;

const CardIconBox = styled.span`
  width: 42px; height: 42px; display: grid; place-items: center;
  border-radius: 12px; background: ${({ $soft }) => $soft}; color: ${({ $color }) => $color};
  font-size: 17px;
`;
const CardContent = styled.span`min-width: 0; display: block;`;

const Chip = styled.span`
  display: inline-flex;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $color }) => $color};
  padding: 3px 7px;
  border-radius: 999px;
  background: ${({ $soft }) => $soft};
`;

const IconCircle = styled.span`
  position: absolute; right: 16px; top: 50%; transform: translateY(-50%);
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f3f6f9;
  color: ${({ $color }) => $color};
  font-size: 11px;
  transition: transform .15s ease, background .15s ease;
  ${Card}:hover & { transform: translate(3px, -50%); background: #eef5fb; }
`;

const Title = styled.h3`
  margin: 0;
  margin-top: 8px;
  font-size: 15px;
  color: #0f172a;
  font-weight: 850;
`;

const Desc = styled.p`
  margin: 0;
  color: #6b7280;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
`;

const EmptyState = styled.div`
  padding: 60px 20px; border: 1px dashed #cbd5e1; border-radius: 18px; background: #fff; text-align: center;
  > svg { color: #94a3b8; font-size: 25px; }
  h3 { margin: 12px 0 4px; color: #0f172a; }
  p { margin: 0; color: #64748b; font-size: 13px; }
  button { margin-top: 16px; padding: 9px 14px; border: 0; border-radius: 9px; background: var(--app-primary); color: #fff; font-weight: 800; cursor: pointer; }
`;
